import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faRefresh, faFilter } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { WorkflowLog } from '../../types/workflow';

interface WorkflowLogsViewerProps {
  logs: WorkflowLog[];
  onClose: () => void;
  onRefresh: () => void;
}

const WorkflowLogsViewer: React.FC<WorkflowLogsViewerProps> = ({
  logs,
  onClose,
  onRefresh
}) => {
  const [filter, setFilter] = useState('all');

  const filteredLogs = logs.filter(log => 
    filter === 'all' || log.type === filter || log.status === filter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Workflow Logs</h2>
          <div className="flex items-center space-x-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">Alle</option>
              <option value="success">Erfolg</option>
              <option value="warning">Warnung</option>
              <option value="error">Fehler</option>
            </select>
            <button
              onClick={onRefresh}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-4">
          <div className="space-y-3">
            {filteredLogs.map((log, index) => (
              <div key={log.id || index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                      <span className="text-sm text-gray-600">{log.type}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString('de-DE')}
                      </span>
                    </div>
                    <p className="text-gray-900">{log.message}</p>
                    {log.details && (
                      <pre className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {filteredLogs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>Keine Logs gefunden</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WorkflowLogsViewer;